import React from 'react'
import { MenuItem, ListSubheader } from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import CreateIcon from '@mui/icons-material/Create'
import MoreButtonMenu from 'components/MoreButtonMenu'
import { SupervisedUserCircle } from '@mui/icons-material'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { TeamManagementField_User$key } from '__generated__/TeamManagementField_User.graphql'
import { TeamManagementField_UpdateUserMutation } from '__generated__/TeamManagementField_UpdateUserMutation.graphql'
import { useSnackbar } from 'hooks/redux'
import useStyles from './styles'

const MANAGED_TEAM_SIZE = {
  none: 'None',
  '1-4': '<5',
  '5-10': '5-10',
  '10-x': '10+',
} as const

type ManagedTeamSizeType = keyof typeof MANAGED_TEAM_SIZE

interface ITeamManagementFieldProps {
  editable: boolean
  user: TeamManagementField_User$key
}

function TeamManagementField(props: ITeamManagementFieldProps) {
  const { classes } = useStyles()
  const { editable, user: userFragmentRef } = props
  const { showSnackbarMessage } = useSnackbar()

  const user = useFragment(graphql`
    fragment TeamManagementField_User on User {
      profile {
        managedTeamSize
      }
    }
  `, userFragmentRef)

  const { execute, loading } = useQuickCommit<TeamManagementField_UpdateUserMutation>(graphql`
    mutation TeamManagementField_UpdateUserMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          profile {
            managedTeamSize
          }
        }
      }
    }
  `)

  const update = async (value: ManagedTeamSizeType) => {
    const result = await execute({
      input: {
        profile: {
          managedTeamSize: value,
        },
      },
    })
    if (result?.updateUser?.user?.profile?.managedTeamSize) {
      showSnackbarMessage('Management experience updated', { level: 'success' })
    }
  }

  const managedTeamSize = user.profile?.managedTeamSize as ManagedTeamSizeType || 'none'
  const userManagedTeam = Boolean(managedTeamSize && managedTeamSize !== 'none')

  if (!editable && !userManagedTeam) {
    return null
  }

  return (
    <div className={userManagedTeam ? classes.enabled : classes.disabled} data-cy="managed-team-size">
      {userManagedTeam && (
        <React.Fragment>
          <SupervisedUserCircle className={classes.icon} />Management ({MANAGED_TEAM_SIZE[managedTeamSize]})
        </React.Fragment>
      )}

      {!userManagedTeam && (
        <React.Fragment>
          <BlockIcon className={classes.icon} />Management
        </React.Fragment>
      )}

      {editable && (
        <MoreButtonMenu
          className={classes.editButton}
          icon={<CreateIcon />}
          data-cy="profile-management"
          disabled={loading}
        >
          <ListSubheader>
            Largest team you managed?
          </ListSubheader>

          {(Object.keys(MANAGED_TEAM_SIZE) as ManagedTeamSizeType[]).map(value => (
            <MenuItem
              value={value}
              key={value}
              data-cy={`managed_team_size-option-${value}`}
              onClick={() => update(value)}
            >
              {MANAGED_TEAM_SIZE[value]}
            </MenuItem>
          ))}
        </MoreButtonMenu>
      )}
    </div>
  )
}

export default React.memo(TeamManagementField)
